import { type JSX,useEffect, useState } from 'react';

import {
    Autocomplete,
    FormControl,
    Link,
    MenuItem,
    Paper,
    TextField,
    Typography } from '@mui/material';
import { useAppDispatch,useAppSelector } from 'app/hooks';
import { ReactComponent as PalmIcon } from 'assets/icons/IconPalm.svg';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchUserProfilesByEmail } from 'common/services/api/UserProfiles/UserProfiles.thunk';
import { UserProfileOemMapping } from 'common/types/Entities';
import { handleListFetchError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { clearUserProfiles, selectUserProfiles } from './userProfilesSlice';

type UserProfilesDropdownProps = {
    value: UserProfileOemMapping | null,
    allowCreateDjUser?: boolean,
    placeholder?: string,
    onChange: (e: unknown, userProfile: UserProfileOemMapping | null) => void
};

const customPaperCaller = (dropdownProps: UserProfilesDropdownProps, fetched: boolean, userProfiles: UserProfileOemMapping[], inputValue: string) => {
    const addDjUserClicked = () => {
        dropdownProps.onChange(null, {
            authority: import.meta.env.VITE_DJ_IDP_ENDPOINT,
            firstName: '',
            lastName: '',
            companyKey: '',
            oemIdPName: 'Dow Jones IDP',
            oemId: '',
            subject: '',
            email: inputValue,
            oemIdPId: '',
            activeIdentityStatus: false
        });
    };

    return function CustomPaper(paperProps: React.HTMLAttributes<HTMLElement>) {
        const createEnabled = fetched && !userProfiles.some((up: UserProfileOemMapping) => up.oemIdPId === import.meta.env.VITE_OEM_DJ_IDP_ID);
        return <Paper>
            {dropdownProps.allowCreateDjUser && createEnabled && <Link
                href="#"
                onMouseDown={addDjUserClicked}
                underline="none">
                <Typography padding={2}>
                + Create Dow Jones IDP User
                </Typography>
            </Link>}
            {paperProps.children}
        </Paper>;
    };
};

export function UserProfilesDropDown(props: Readonly<UserProfilesDropdownProps>): JSX.Element {
    const [inputValue, setInputValue] = useState<string>(props.value?.email ?? '');
    const [loading, setLoading] = useState<boolean>(false);
    const [fetched, setFetched] = useState<boolean>(false);
    const [shouldShowDJIdpCustomLink, setShouldShowDJIdpCustomLink] = useState<boolean>(true);
    const dispatch = useAppDispatch();
    const userProfiles = useAppSelector(selectUserProfiles);

    const { enqueueSnackbar } = useSnackbar();

    const handleInputValue = (_e: unknown, value: string) => {
        setInputValue(value);
    };

    useEffect(() => {
        setFetched(false);
        if (userProfiles.length) {
            dispatch(clearUserProfiles());
        }
        props.onChange(null, null);
    }, [inputValue.toLowerCase().trim()]);

    const handleKeyDown: React.KeyboardEventHandler<HTMLDivElement> | undefined = e => {
        if (!inputValue) {
            return;
        }
        if (e.key === 'Enter') {
            setLoading(true);
            dispatch(fetchUserProfilesByEmail(inputValue.trim()))
                .unwrap()
                .catch(error => {
                    setFetched(false);
                    setLoading(false);
                    setShouldShowDJIdpCustomLink(false);
                    handleListFetchError(extractStatusCode(error.message), 'User Profiles', enqueueSnackbar);
                })
                .finally(() => {
                    setFetched(true);
                    setLoading(false);
                });
        }
    };

    const customPaperToRender = shouldShowDJIdpCustomLink ? customPaperCaller(props, fetched, userProfiles, inputValue) : undefined;

    return (
        <FormControl>
            <Autocomplete
                handleHomeEndKeys={false}
                filterOptions={(options, filterState) => (options.filter(opt => {
                    return opt.email.trim().toLowerCase().includes(filterState.inputValue.trim().toLowerCase());
                }))}
                value={props.value}
                loading={loading}
                PaperComponent={customPaperToRender}
                onInputChange={handleInputValue}
                noOptionsText={
                    fetched
                        ? 'User not found'
                        : props.placeholder ?? 'Type full e-mail and press Enter'
                }
                onKeyDown={handleKeyDown}
                onChange={props.onChange}
                options={userProfiles}
                getOptionLabel={userProfile => userProfile ? userProfile.email : ''}
                isOptionEqualToValue={(opt, val) => val
                    ? opt?.email?.toLowerCase().trim() === val?.email?.toLowerCase().trim() && opt?.authority === val?.authority
                    : false}
                renderInput={(params) => <TextField {...params} label="User Email" />}
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                renderOption={({ key: _, ...params }, userProfile) =>
                    <MenuItem
                        key={`${userProfile.email}-${userProfile.oemId}`}
                        {...params}
                        value={userProfile.email}
                        style={{ display: 'block' }}
                    >
                        <Typography>
                            {`${userProfile.firstName} ${userProfile.lastName}`}
                            {userProfile.oemId && <PalmIcon />}
                        </Typography>
                        <Typography
                            variant="body1"
                            gutterBottom
                            color="info.light"
                            noWrap
                        >
                            {`${userProfile.email} | ${userProfile.oemIdPName}`}
                        </Typography>
                    </MenuItem>
                }
            />
        </FormControl>
    );
}
